import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {ErrorMessage} from 'shells/form/error-message';
import {FormField} from 'shells/form/form-field';
import {TextfieldWithCheckbox} from 'shells/form/textfield-with-checkbox';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {TextField} from 'shells/textfield/textfield';
import {MarketingTitle} from 'shells/typography/marketing-title';
import {ModalBody} from 'shells/typography/modal-body';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {getUtmPreviewLink} from './get-utm-preview-link';

import styles from './utm-form-modal.module.css';

type EditionUtmCampaignData = {
    id: string;
    useDefaultCampaignValue: boolean;
    utmCampaign: string;
    utmContent: string;
};

type Props = {
    isOpen: boolean;
    editions: Edition[];
    source: string | null | undefined;
    medium: string | null | undefined;
    onClose: () => void;
    onSave: (
        source: string | null | undefined,
        medium: string | null | undefined,
        editionUtmCampaigns: EditionUtmCampaignData[]
    ) => Promise<any>;
};

export function UtmFormModal(props: Props) {
    /* Campaign level parameters */
    const [campaignSource, setCampaignSource] = React.useState<string | null | undefined>(
        props.source
    );
    const [campaignMedium, setCampaignMedium] = React.useState<string | null | undefined>(
        props.medium
    );

    /* Campaign parameters specific to each edition */
    const [editionUtmCampaigns, setEditionUtmCampaigns] = React.useState<EditionUtmCampaignData[]>(
        props.editions.length
            ? props.editions.map((edition) => {
                  const useDefaultCampaign = edition.utmParameters.campaign === null;

                  // The newsletters are handled differently because we want the name of the specific
                  // edition in the URL, instead of the overall compaign name.
                  const nameHolder =
                      edition.campaign.type === 'NEWSLETTER' ? edition.name : edition.campaign.name;

                  return {
                      id: edition.id,
                      useDefaultCampaignValue: useDefaultCampaign,
                      utmCampaign:
                          (useDefaultCampaign
                              ? edition.defaultUtmParameters.campaign
                              : edition.utmParameters.campaign) || '',
                      utmContent: nameHolder,
                  };
              })
            : []
    );

    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    function onClose() {
        setIsSaving(false);
        props.onClose();
    }

    const hasError = !hasAllCampaigns() && !campaignSource && !campaignMedium;

    /* Returns relevant error message */
    function getErrorMessage() {
        if (!campaignSource && !campaignMedium && editionUtmCampaigns.length > 1) {
            // If only using campaign and one or more is incomplete
            return (
                <ErrorMessage>
                    If you’re only using <b>utm_campaign</b>, every message in your drip sequence
                    must have a UTM parameter.
                </ErrorMessage>
            );
        } else {
            // All fields are missing
            return (
                <ErrorMessage>
                    You’ll need at least one UTM parameter to send this message! If you’d prefer not
                    to use them, uncheck “Add UTM parameters to all emails sent from Nutshell” in
                    the Details section.
                </ErrorMessage>
            );
        }
    }

    /* Returns true if all editions have a campaign value, false if not or if no editions */
    function hasAllCampaigns() {
        let hasCampaigns = true;

        editionUtmCampaigns.forEach((edition) => {
            if (edition.utmCampaign === '') {
                hasCampaigns = false;
            }
        });

        return hasCampaigns && editionUtmCampaigns.length;
    }

    /* Returns name of edition for given editionId */
    function getEditionNameComponent(editionId: string) {
        const edition = props.editions.find((ed) => ed.id === editionId);
        if (edition) {
            return props.editions.length === 1 ? undefined : (
                <span className={styles['edition-name']}>
                    <ModalBody>{edition.name}</ModalBody>
                </span>
            );
        }

        return undefined;
    }

    /* Returns default campaign value for given editionId */
    function getEditionDefaultCampaign(editionId: string) {
        const edition = props.editions.find((ed) => ed.id === editionId);
        if (edition) {
            return edition.defaultUtmParameters.campaign
                ? edition.defaultUtmParameters.campaign
                : '';
        }

        return '';
    }

    /* Handles updating the utm campaign for the specified edition */
    // @ts-expect-error
    function handleUpdateEditionUtmCampaign(editionId, newCampaignValue) {
        editionUtmCampaigns.forEach((edition, index) => {
            if (edition.id === editionId) {
                const utmCampaigns = [...editionUtmCampaigns];
                // @ts-expect-error
                utmCampaigns[index] = {
                    ...utmCampaigns[index],
                    utmCampaign: newCampaignValue,
                };
                setEditionUtmCampaigns(utmCampaigns);
            }
        });
    }

    /* Handles when the use default checkbox is toggled for the specified edition */
    // @ts-expect-error
    function handleToggleUseDefaultUtmCampaign(editionId) {
        editionUtmCampaigns.forEach((edition, index) => {
            if (edition.id === editionId) {
                const utmCampaigns = [...editionUtmCampaigns];
                // @ts-expect-error
                utmCampaigns[index] = {
                    ...utmCampaigns[index],
                    // @ts-expect-error
                    useDefaultCampaignValue: !utmCampaigns[index].useDefaultCampaignValue,
                    // @ts-expect-error
                    utmCampaign: !utmCampaigns[index].useDefaultCampaignValue
                        ? getEditionDefaultCampaign(edition.id)
                        : // @ts-expect-error
                          utmCampaigns[index].utmCampaign,
                };
                setEditionUtmCampaigns(utmCampaigns);
            }
        });
    }

    /* Link previews */
    function getLinkPreviews() {
        if (props.editions.length) {
            return editionUtmCampaigns.map((edition) => {
                return (
                    <div key={`preview_link_${edition.id}`}>
                        <ModalBody>
                            {props.editions.length === 1 ? undefined : (
                                <>{getEditionNameComponent(edition.id)}: </>
                            )}
                            <span className={styles['fake-link']}>
                                {getUtmPreviewLink('yourlink.com', {
                                    campaign: edition.utmCampaign,
                                    source: campaignSource,
                                    medium: campaignMedium,
                                    content: edition.utmContent,
                                })}
                            </span>
                        </ModalBody>
                    </div>
                );
            });
        }

        // Show base link without campaign
        return (
            <span className={styles['fake-link']}>
                {getUtmPreviewLink('yourlink.com', {
                    campaign: '',
                    source: campaignSource,
                    medium: campaignMedium,
                })}
            </span>
        );
    }

    /* Form fields for each edition's utm_campaign */
    const campaignFormFields = editionUtmCampaigns.map((edition) => {
        return (
            <div key={edition.id} className={styles['utm-campaign-field']}>
                {getEditionNameComponent(edition.id)}
                <FormField innerId={`utm_campaign_${edition.id}`} noMargin={true}>
                    <TextfieldWithCheckbox
                        id={`utm_campaign_${edition.id}`}
                        name={`utm_campaign_${edition.id}`}
                        placeholder='Add an optional utm_campaign…'
                        hasError={
                            hasError && editionUtmCampaigns.length > 1 && !edition.utmCampaign
                        }
                        textfieldValue={edition.utmCampaign}
                        onTextfieldChange={(value) =>
                            handleUpdateEditionUtmCampaign(edition.id, value)
                        }
                        checkbox={{
                            checked: edition.useDefaultCampaignValue,
                            onChange: () => {
                                handleToggleUseDefaultUtmCampaign(edition.id);
                            },
                            label: 'Use message name (recommended)',
                        }}
                    />
                </FormField>
            </div>
        );
    });

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={onClose}
            canClickOutsideToClose={false}
            closeButton={true}
            maxHeight={700}
            preventBodyScroll={true}
            headerText='UTM parameters'
        >
            <form
                className='flex justify-sb flex-dir-col full-height'
                onSubmit={(event) => {
                    event.preventDefault();
                    setIsSaving(true);

                    props.onSave(campaignSource, campaignMedium, editionUtmCampaigns).then(() => {
                        setIsSaving(false);
                    });
                }}
            >
                <div className={styles.container}>
                    <div className={styles['form-container']}>
                        <ModalBody>
                            Add your Google Analytics UTM parameters to emails sent from Nutshell!
                            We’ll add these parameters to every link in your message so that you can
                            use Google Analytics to understand the actions that recipients take on
                            your website after they click a link.
                        </ModalBody>
                        <div className={styles['preview-links-container']}>
                            <MarketingTitle>Link preview</MarketingTitle>
                            {hasError ? getErrorMessage() : getLinkPreviews()}
                        </div>
                        <FormField innerId='utm_source' label='utm_source'>
                            <TextField
                                id='utm_source'
                                // @ts-expect-error
                                name='utm_source'
                                placeholder='Add an optional utm_source…'
                                value={campaignSource || ''}
                                onChange={setCampaignSource}
                            />
                        </FormField>
                        <FormField innerId='utm_medium' label='utm_medium'>
                            <TextField
                                id='utm_medium'
                                // @ts-expect-error
                                name='utm_medium'
                                placeholder='Add an optional utm_medium…'
                                value={campaignMedium || ''}
                                onChange={setCampaignMedium}
                            />
                        </FormField>
                        <FormField innerId='utm_campaign_fields' label='utm_campaign'>
                            {props.editions.length ? (
                                <div id='utm_campaign_fields'>{campaignFormFields}</div>
                            ) : (
                                <div className={styles['help-text']}>
                                    This drip sequence does not have any messages yet. Each message
                                    will have its own unique <span>utm_campaign</span> parameter.
                                </div>
                            )}
                        </FormField>
                    </div>
                    <FooterBar
                        left={
                            <Button variant='text' size='big' onClick={onClose}>
                                Cancel
                            </Button>
                        }
                        right={
                            <SaveButton
                                // @ts-expect-error
                                type='submit'
                                isSaving={isSaving}
                                saveCompleteTimeout={2000}
                                disabled={hasError}
                                onClearSaveComplete={onClose}
                            />
                        }
                    />
                </div>
            </form>
        </Modal>
    );
}
